import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { ROUTE_MAIN_PATHS } from '@app/app.routes.paths';
import { Logger } from '@app/services/logger/logger.service';
import { noEmptyObject } from '@app/utils';
import { formatEmailRegex } from '@app/utils/format-email-regex/format-email-regex';
import { environment } from '@environments/_current/environment';
import {
  EnterpriseEntityConfigs,
  EnterpriseEntityFeatures,
  EnterpriseTenant,
  EnterpriseTenantContract,
  GympassProductTier,
  LocalStorageKey,
  ProvisioningTypes,
  TenantResources,
  TenantTheme,
} from '@skyfit/core-data-library';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

export interface Config {
  slugs: {
    value: string[];
  };
  emailDomains: {
    value: string[];
  };
  memberPaid: {
    value: boolean;
    config: {
      priceMonthly: string;
      priceIdMonthly: string;
    };
  };
}

export interface ConfigTenant {
  name: string;
  description: string;
  config: Config;
}

export interface TenantConfigurationGeneral {
  name: string;
  description: string;
  provisioningType: ProvisioningTypes;
  slugs: string;
  emails: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdminTenantService implements OnDestroy {
  private readonly subscription = new Subscription();
  private readonly tenantSource = new BehaviorSubject(null);

  tenant = signal(undefined);
  dirtyTenant = signal(undefined);
  tenant$: Observable<any> = this.tenantSource.asObservable();
  tenantMonthlyPricing = signal({});
  tenants = signal<EnterpriseTenant[]>([]);
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);

  loadTenants(): Observable<EnterpriseTenant[]> {
    const apiUrl = `${environment.aaptivServiceUrl}/api/v2/admin/enterpriseTenants`;
    return this.http.get<EnterpriseTenant[]>(apiUrl).pipe(
      tap({
        next: (list) => this.tenants.set(list),
        error: (err) => this.logger.error(err),
      }),
      catchError((err) => {
        this.logger.error(err);
        return of([]);
      })
    );
  }

  refreshTenants(): Observable<EnterpriseTenant[]> {
    return this.loadTenants();
  }

  getTenantIdFromStorage(): string | null {
    return localStorage.getItem(LocalStorageKey.userTenantId);
  }

  setTenantIdInStorage(tenant: EnterpriseTenant) {
    if (!tenant?.id) {
      localStorage.removeItem(LocalStorageKey.userTenantId);
    } else {
      localStorage.setItem(LocalStorageKey.userTenantId, tenant.id);
      this.setTenant(tenant);
      this.tenant.set(tenant);
    }
  }

  setDefaultTenantId(tenantId: string | undefined): void {
    if (!tenantId) {
      localStorage.removeItem(LocalStorageKey.defaultTenantId);
    } else {
      localStorage.setItem(LocalStorageKey.defaultTenantId, tenantId);
    }
  }

  getDefaultTenantId(): string | null {
    return localStorage.getItem(LocalStorageKey.defaultTenantId);
  }

  clearTenantFromStorage() {
    localStorage.removeItem(LocalStorageKey.userTenantId);
    localStorage.removeItem(LocalStorageKey.defaultTenantId);
  }

  getTenant() {
    return this.tenant();
  }

  getProvisioningType(): string | undefined {
    const tenant = this.getTenant();
    if (!tenant) {
      return undefined;
    }

    return (
      tenant.config?.provisioningType?.value ??
      (tenant as EnterpriseTenant & { provisioningType?: string })
        .provisioningType
    );
  }

  isSsoOAuthTenant(): boolean {
    const provisioningType = this.getProvisioningType();
    return (
      provisioningType === ProvisioningTypes.SSO_OAuth ||
      provisioningType === 'SSO/OAuth'
    );
  }

  getDefaultLandingRoute(): string {
    return this.isSsoOAuthTenant()
      ? ROUTE_MAIN_PATHS.users
      : ROUTE_MAIN_PATHS.home;
  }

  setTenant(tenant: EnterpriseTenant): void {
    this.tenantSource.next(tenant);
    this.tenant.set(tenant);
    this.dirtyTenant.set(structuredClone(tenant));
  }

  clearTenant(): void {
    this.setTenant(undefined);
    this.dirtyTenant.set(undefined);
    this.clearTenantFromStorage();
  }

  getTenantId(): string {
    return this.getTenant()?.id;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getTenantFromBackend(): Observable<EnterpriseTenant> {
    const userTenantId = this.getTenantIdFromStorage();
    const url = `${environment.aaptivServiceUrl}/api/v1/admin/enterpriseTenants/${userTenantId}`;
    const tenant$ = this.http.get<EnterpriseTenant>(url).pipe(
      tap((tenant) => {
        this.setTenant(tenant);
        this.tenant.set(tenant);
      })
    );

    return tenant$;
  }

  getAdminThemeFromBackend(): Observable<TenantTheme> {
    const tenantId = this.getTenantIdFromStorage();
    const apiUrl = `${environment.aaptivServiceUrl}/api/v1/admin/enterpriseTenants/${tenantId}/theme/adminTheme`;
    return this.http.get<TenantTheme>(apiUrl);
  }

  updateTenantAdminTheme(updatedTheme: TenantTheme): Observable<TenantTheme> {
    const tenantId = this.getTenantId();
    updatedTheme.name = this.getTenant().name + ' adminTheme';
    const apiUrl = `${environment.aaptivServiceUrl}/api/v1/admin/enterpriseTenants/${tenantId}/theme/adminTheme`;
    return this.http.put<TenantTheme>(apiUrl, updatedTheme);
  }

  getTenants(): Observable<EnterpriseTenant[]> {
    const apiUrl = `${environment.aaptivServiceUrl}/api/v2/admin/enterpriseTenants`;
    return this.http.get<EnterpriseTenant[]>(apiUrl);
  }

  getTenantFeatures() {
    const tenant = this.getTenant();
    return tenant.features;
  }

  hasFeature(featureName: string): boolean {
    const tenant = this.getTenant();
    console.log('tenant', tenant);
    const features = this.getTenant()?.features as
      | Record<string, { value?: boolean } | undefined>
      | undefined;
    return !!features?.[featureName]?.value;
  }

  getTenantConfig() {
    const tenant = this.getTenant();
    return tenant.config;
  }

  updateDirtyTenant(tenant: EnterpriseTenant): void {
    this.dirtyTenant.set(tenant);
  }

  updateTenantGeneralInfo(
    general: TenantConfigurationGeneral
  ): Observable<EnterpriseTenant> {
    this.dirtyTenant.update((prev) => {
      return noEmptyObject({
        ...(prev ?? {}),
        name: general.name,
        description: general.description,
        config: {
          ...(prev?.config ?? {}),
          slugs: {
            ...(prev?.config?.slugs ?? {}),
            value: general.slugs?.split(',').map((s) => s.trim()),
          },
          emailDomains: {
            ...(prev?.config?.emailDomains ?? {}),
            value: general.emails?.split(',').map((e) => e.trim()),
          },
          provisioningType: { value: general.provisioningType },
          emailRegex: {
            value: formatEmailRegex(
              general.emails?.split(',').map((e) => e.trim())
            ),
          },
        },
        provisioningType: general.provisioningType,
      });
    });
    return this.putTenant(this.dirtyTenant());
  }

  updateTenantMonthlyPrice(
    monthlyPriceId: string
  ): Observable<EnterpriseTenant> {
    const monthlyPrice = this.tenantMonthlyPricing()[monthlyPriceId];

    this.dirtyTenant.update((prev) => {
      return {
        ...prev,
        config: {
          ...prev.config,
          memberPaid: {
            ...(prev.config?.memberPaid ?? {}),
            value: monthlyPriceId !== 'None',
            config: {
              ...(prev.config?.memberPaid?.config ?? {}),
              priceMonthly: monthlyPrice,
              priceIdMonthly: monthlyPriceId,
            },
          },
        },
      };
    });
    return this.putTenant(this.dirtyTenant());
  }

  clearDirtyTenant(): void {
    this.dirtyTenant.set(undefined);
  }

  saveDirtyTenant() {
    this.clearDirtyTenant();
  }

  updateTenantConfig(
    config: EnterpriseEntityConfigs
  ): Observable<EnterpriseTenant> {
    const tenantId = this.getTenantId();
    const tenant = this.getTenant();
    if (!tenantId || !tenant) {
      this.logger.warn('Tenant ID or tenant is not available');
      return throwError(() => new Error('Tenant ID or tenant is missing'));
    }
    const updatedTenant = {
      ...tenant,
      config: {
        ...tenant.config,
        ...config,
      },
    };
    return this.putTenant(updatedTenant);
  }

  removeTenantConfig(
    config: EnterpriseEntityConfigs
  ): Observable<EnterpriseTenant> {
    const tenantId = this.getTenantId();
    const tenant = this.getTenant();
    if (!tenantId || !tenant) {
      this.logger.warn('Tenant ID or tenant is not available');
      return throwError(() => new Error('Tenant ID or tenant is missing'));
    }
    const updatedTenant = {
      ...tenant,
      config,
    };
    return this.putTenant(updatedTenant);
  }

  updateTenantFeatures(
    features: EnterpriseEntityFeatures
  ): Observable<EnterpriseTenant> {
    const tenantId = this.getTenantId();
    const tenant = this.getTenant();
    if (!tenantId || !tenant) {
      this.logger.warn('Tenant ID or tenant is not available');
      return throwError(() => new Error('Tenant ID or tenant is missing'));
    }

    return this.putTenant({
      ...tenant,
      features: {
        ...tenant.features,
        ...features,
      },
    });
  }

  putTenant(
    tenant: EnterpriseTenant,
    shouldSetTenant = true
  ): Observable<EnterpriseTenant> {
    const apiUrl = `${environment.aaptivServiceUrl}/api/v1/admin/enterpriseTenants/${tenant.id}`;
    return this.http.put<EnterpriseTenant>(apiUrl, { ...tenant }).pipe(
      switchMap((savedTenant) => {
        if (shouldSetTenant) {
          this.setTenant(savedTenant);
        }
        return this.loadTenants().pipe(
          map(() => savedTenant),
          catchError(() => {
            this.logger.error('Error reloading tenants');
            return of(savedTenant);
          })
        );
      })
    );
  }

  createTenant(tenant: EnterpriseTenant): Observable<EnterpriseTenant> {
    const apiUrl = `${environment.aaptivServiceUrl}/api/v1/admin/enterpriseTenants/`;

    return this.http
      .post<EnterpriseTenant>(apiUrl, {
        ...tenant,
        features: {},
        contracts: [],
      })
      .pipe(
        switchMap((createdTenant) => {
          return this.loadTenants().pipe(
            map(() => createdTenant),
            catchError(() => {
              this.logger.error('Error reloading tenants');
              return of(createdTenant);
            })
          );
        })
      );
  }
  getEmployerProvider() {
    const apiUrl = `${environment.aaptivServiceUrl}/api/v1/employerProvider`;
    return this.http.get(apiUrl).pipe(
      map((response: Record<string, unknown>[]) => {
        return response;
      })
    );
  }

  getTenantProducts(): Observable<GympassProductTier[]> {
    const apiUrl = `${environment.aaptivServiceUrl}/api/v1/gympass/products`;
    return this.http.get(apiUrl).pipe(
      map((response: GympassProductTier[]) => {
        return response;
      })
    );
  }

  getTenantResources(tenantId: string): Observable<TenantResources> {
    const apiUrl = `${environment.aaptivServiceUrl}/api/v1/enterpriseTenants/${tenantId}/resources`;
    return this.http.get<TenantResources>(apiUrl);
  }

  setTenantPricing() {
    const apiUrl = `${environment.aaptivServiceUrl}/api/v1/stripe/prices`;
    return this.http.get(apiUrl).pipe(
      map((response: Record<string, unknown>) => {
        const monthlyPricing = (
          response?.data as Record<string, unknown>[]
        )?.reduce((acc, next) => {
          if (
            (next.recurring as Record<string, unknown>)?.interval === 'month'
          ) {
            return {
              ...acc,
              [next.id as string]: (next.unit_amount as number) / 100,
            };
          }

          return acc;
        }, {});

        this.tenantMonthlyPricing.set({
          none: 0,
          ...monthlyPricing,
        });

        return this.tenantMonthlyPricing;
      })
    );
  }

  getFriendsAndFamilyChildTenant(): Observable<EnterpriseTenant> {
    const friendsAndFamilyTenantId =
      this.getTenant().features?.friendsAndFamily?.config?.tenantId;

    if (!friendsAndFamilyTenantId) {
      this.logger.error('No Child Tenant Id');
      return throwError(() => new Error('No Child Tenant Id'));
    }

    const url = `${environment.aaptivServiceUrl}/api/v1/admin/enterpriseTenants/${friendsAndFamilyTenantId}`;
    return this.http.get<EnterpriseTenant>(url).pipe(
      catchError((error) => {
        this.logger.error(
          `Error getting Friends and Family Child Tenant: ${error}`
        );
        return throwError(
          () => new Error('Error getting Friends and Family Child Tenant')
        );
      })
    );
  }

  updateTenantContracts(
    contract: EnterpriseTenantContract
  ): Observable<EnterpriseTenant> {
    const tenantId = this.getTenantId();
    const tenant = this.getTenant();
    if (!tenantId || !tenant) {
      this.logger.warn('Tenant ID or tenant is not available');
      return throwError(() => new Error('Tenant ID or tenant is missing'));
    }

    const currentContracts = tenant.contracts ?? [];
    const contractIndex = contract._id
      ? currentContracts.findIndex((c) => c._id === contract._id)
      : -1;

    const newContracts = [...currentContracts];
    if (contractIndex > -1) {
      // Replace existing contract
      newContracts[contractIndex] = contract;
    } else {
      // Add new contract
      newContracts.push(contract);
    }

    newContracts.sort((a, b) => {
      return new Date(b.start).getTime() - new Date(a.start).getTime();
    });

    return this.putTenant({
      ...tenant,
      contracts: newContracts,
    });
  }
}
